import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import CrossIcon from "../../assets/svg/icons/common/cross";
import Plus from "../../assets/svg/icons/common/Plus";
import { useClickAway } from "../../hooks/general/useClickAway";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import Select from "../form/select";
import Spinner from "../Loaders/Spinner";
import EquipmentSelector from "./equipment-selector";
import EquipmentConfigurator from "./equipment-configurator";
import useDomainStore from "../../stores/domain";
import ButtonIcon from "../ButtonIcon/ButtonIcon";
import CaretLeftIcon from "../../assets/svg/icons/common/CaretLeft";
import styles from "./styles.module.scss";

const BrandItem = ({
  brand,
  image,
  handleAddEquipment,
  handleRemoveAllEquipments,
  selectedEquipments,
  selectedEquipmentsInferCount = false,
  noShadow = false,
  disabled = false,
  equipmentConfigurator = false,
  allSelectedEquipments = [],
  allEquipments = [],
  equipmentBeingConfigured = null,
  handleAddConfiguredEquipment = null,
  onEquipmentChange = null,
  onClickOutside = null,
}) => {
  const { isKurvDomain } = useDomainStore();
  const [configuring, setConfiguring] = useState(false); // remove
  const isSelected = selectedEquipments?.length > 0;
  const totalQuantity = selectedEquipmentsInferCount
    ? selectedEquipments?.length
    : isSelected &&
      selectedEquipments.reduce((acc, curr) => acc + (curr.quantity ?? 1), 0);
  const selectedClass = isSelected ? styles.selected : "";
  const configuringClass = configuring ? styles.configuring : "";
  const configAreaRef = useRef(null);
  const [imageLoading, setImageLoading] = useState(true);
  const isMobile = useMediaQuery(768);

  const handleStopConfiguring = () => {
    setConfiguring(false);
    onClickOutside?.();
  };

  const equipmentRef = useClickAway(() => {
    if (configuring && !isMobile) {
      handleStopConfiguring();
    }
  });
  
  return (
    <article
      className={`${styles.equipment} ${selectedClass} ${configuringClass} ${
        noShadow && styles.noShadow
      } ${disabled && styles.disabled} ${isKurvDomain ? styles.kurv_theme : ""}`}
      ref={equipmentRef}
    >
      {isSelected && (
        <span
          className={styles.selectedCircle}
          onClick={() => {
            if (!disabled) {
              handleRemoveAllEquipments(selectedEquipments.map((e) => e.id));
            }
          }}
        >
          <span className={styles.count}>{totalQuantity}</span>
          <CrossIcon />
        </span>
      )}
      <div
        className={styles.imageContainer}
        onClick={() => {
          if (!disabled) {
            setConfiguring((prev) => !prev);
          }
        }}
      >
        {imageLoading && (
          <span className={styles.spinnerContainer}>
            <Spinner />
          </span>
        )}

        <img
          src={image}
          alt={`${brand.name} logo`}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
          style={{ display: imageLoading ? "none" : "block" }}
        />
        {!isSelected && (
          <div className={styles.selectionCircle}>
            <Plus />
          </div>
        )}
      </div>

      <div className={styles.nameArea}>
        <h1 className={styles.title}>{brand.name}</h1>
        {!isSelected && (
          <p className={styles.description}>
            {brand?.equipments?.length} model
            {brand?.equipments?.length > 1 ? "s" : ""} available
          </p>
        )}
      </div>

      {configuring && (
        <div className={styles.configurationArea} ref={configAreaRef}>
          {!equipmentConfigurator && (
            <EquipmentSelector
              brand={brand}
              handleAddEquipment={handleAddEquipment}
              selectedEquipments={selectedEquipments}
            />
          )}

          {equipmentConfigurator && (
            <>
              <Select
                id={brand.id}
                onChange={handleAddEquipment}
                options={brand.equipments.map((model) => ({
                  value: model.id,
                  label: model.equipment_name,
                }))}
                value={equipmentBeingConfigured?.id}
                placeholder="Select model"
                trackChanges
                showSelectAll={false}
                selectedTextPrefix="Model: "
              />

              {equipmentBeingConfigured && (
                <EquipmentConfigurator
                  type={brand.equipment_type}
                  equipment={equipmentBeingConfigured}
                  allSelectedEquipments={allSelectedEquipments}
                  allEquipments={allEquipments}
                  onEquipmentChange={onEquipmentChange}
                  handleAddConfiguredEquipment={(type, quantity) => {
                    handleAddConfiguredEquipment(type, quantity);
                    setConfiguring(false);
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      {configuring &&
        createPortal(
          <div
            className={`${styles.configurationAreaMobile} ${styles.open}`}
            aria-modal="true"
            role="dialog"
          >
            <header>
              <div>
                <ButtonIcon
                  size="small"
                  bgColor="transparent"
                  textColor="#000"
                  onClick={handleStopConfiguring}
                  className={styles.caretLeftIcon}
                >
                  <CaretLeftIcon />
                </ButtonIcon>
                <h2 className={styles.title}>{brand.name}</h2>
                {totalQuantity > 0 && (
                  <span className={styles.count}>{totalQuantity}</span>
                )}
              </div>

              {totalQuantity > 0 && (
                <ButtonIcon
                  size="small"
                  bgColor="transparent"
                  textColor="#4c70ef"
                  onClick={() => {
                    handleRemoveAllEquipments(
                      selectedEquipments.map((e) => e.id)
                    );
                    handleStopConfiguring();
                  }}
                  className={styles.caretLeftIcon}
                >
                  Remove all
                </ButtonIcon>
              )}
            </header>

            <main className={styles.configurationAreaMobileContainer}>
              <div className={styles.imageContainer}>
                {imageLoading && (
                  <span className={styles.spinnerContainer}>
                    <Spinner />
                  </span>
                )}

                <img
                  src={image}
                  alt={`${brand.name} logo`}
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                  style={{ display: imageLoading ? "none" : "block" }}
                />
              </div>

              <div className={styles.configurationArea} ref={configAreaRef}>
                {!equipmentConfigurator && (
                  <EquipmentSelector
                    brand={brand}
                    handleAddEquipment={handleAddEquipment}
                    selectedEquipments={selectedEquipments}
                  />
                )}

                {equipmentConfigurator && (
                  <>
                    <Select
                      id={brand.id}
                      onChange={handleAddEquipment}
                      options={brand.equipments.map((model) => ({
                        value: model.id,
                        label: model.equipment_name,
                      }))}
                      value={equipmentBeingConfigured?.id}
                      placeholder="Select model"
                      trackChanges
                      showSelectAll={false}
                      selectedTextPrefix="Model: "
                    />

                    {equipmentBeingConfigured && (
                      <EquipmentConfigurator
                        type={brand.equipment_type}
                        equipment={equipmentBeingConfigured}
                        allSelectedEquipments={allSelectedEquipments}
                        allEquipments={allEquipments}
                        onEquipmentChange={onEquipmentChange}
                        handleAddConfiguredEquipment={(type, quantity) => {
                          handleAddConfiguredEquipment(type, quantity);
                          setConfiguring(false);
                        }}
                        buttonClassName={styles.buttonContainer}
                        containerClassName={styles.configurationContainer}
                      />
                    )}
                  </>
                )}
              </div>
            </main>
          </div>,
          document.body
        )}
    </article>
  );
};

export default BrandItem;
